import React, { useEffect, useRef } from 'react';
import L, { Map as LeafletMap, Polyline as LeafletPolyline, Marker as LeafletMarker } from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface TripPathMapProps {
  // Array of [lat, lng]
  coords: Array<[number, number]>;
  height?: string | number;
  // Optional custom end marker icon URL (e.g., taxi for ongoing trips)
  endIconUrl?: string;
  endIconSize?: [number, number];
}

const TripPathMap: React.FC<TripPathMapProps> = ({ coords, height = 320, endIconUrl, endIconSize }) => {
  const mapRef = useRef<LeafletMap | null>(null);
  const pathRef = useRef<LeafletPolyline | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const startMarkerRef = useRef<LeafletMarker | null>(null);
  const endMarkerRef = useRef<LeafletMarker | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // If no coords, don't render map
    if (!coords || coords.length === 0) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        pathRef.current = null;
        startMarkerRef.current = null;
        endMarkerRef.current = null;
      }
      return;
    }

    const start = coords[0];
    const end = coords[coords.length - 1];

    if (!mapRef.current) {
      const map = L.map(containerRef.current).setView(start, 14);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      const poly = L.polyline(coords, { color: '#2563eb', weight: 4 }).addTo(map);
      map.fitBounds(poly.getBounds(), { padding: [20, 20] });

      // Start and end markers using SVG assets in public/markers
      const startIcon = new L.Icon({
        iconUrl: '/markers/marker-green.svg',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -28],
      });
      const endIcon = new L.Icon({
        iconUrl: endIconUrl || '/markers/marker-red.svg',
        iconSize: endIconSize || [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -28],
      });
      startMarkerRef.current = L.marker(start, { icon: startIcon }).addTo(map);
      endMarkerRef.current = L.marker(end, { icon: endIcon }).addTo(map);

      mapRef.current = map;
      pathRef.current = poly;
    } else {
      // Update path and bounds
      if (pathRef.current) {
        pathRef.current.setLatLngs(coords as any);
        mapRef.current.fitBounds(pathRef.current.getBounds(), { padding: [20, 20] });
      } else {
        pathRef.current = L.polyline(coords, { color: '#2563eb', weight: 4 }).addTo(mapRef.current);
        mapRef.current.fitBounds(pathRef.current.getBounds(), { padding: [20, 20] });
      }

      // Update markers (move to new positions)
      if (startMarkerRef.current) {
        startMarkerRef.current.setLatLng(start as any);
      } else if (mapRef.current) {
        const startIcon = new L.Icon({
          iconUrl: '/markers/marker-green.svg',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -28],
        });
        startMarkerRef.current = L.marker(start, { icon: startIcon }).addTo(mapRef.current);
      }

      if (endMarkerRef.current) {
        endMarkerRef.current.setLatLng(end as any);
      } else if (mapRef.current) {
        const endIcon = new L.Icon({
          iconUrl: endIconUrl || '/markers/marker-red.svg',
          iconSize: endIconSize || [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -28],
        });
        endMarkerRef.current = L.marker(end, { icon: endIcon }).addTo(mapRef.current);
      }
    }
  }, [coords, endIconUrl, endIconSize]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        pathRef.current = null;
        startMarkerRef.current = null;
        endMarkerRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ height, width: '100%', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
    />
  );
};

export default TripPathMap;
